import React from "react";
import { useParams } from "react-router-dom";
import { Heading, Subheading } from "../../design/heading";
import { Badge } from "../../design/badge";
import { ChevronLeftIcon } from "@heroicons/react/16/solid";
import { Link } from "../../design/link";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import { Divider } from "../../design/divider";
import { useAttorney } from "../../contexts/AttorneyContext";
import {
  getAttorneyDisplayName,
  ATTORNEY_PRACTICE_AREA_LABELS,
} from "@visitproof/types";
import EditAttorneyButton from "./components/EditAttorneyButton";
import DeactivateAttorneyButton from "./components/DeactivateAttorneyButton";
import AttorneyCasesList from "./components/AttorneyCasesList";

const Breadcrumb: React.FC = () => (
  <div className="max-lg:hidden">
    <Link
      href="/attorneys"
      className="inline-flex items-center gap-2 transition-colors text-sm/6 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
    >
      <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
      Attorneys
    </Link>
  </div>
);

const AttorneyDetailPage: React.FC = () => {
  const { attorneyId } = useParams<{ attorneyId: string }>();
  const { attorneys, loading } = useAttorney();

  const attorney = attorneys.find((a) => a.id === attorneyId);

  if (loading.list) {
    return <LoadingView />;
  }

  if (!attorney) {
    return <ErrorView error={new Error("Attorney not found")} />;
  }

  const displayName = getAttorneyDisplayName(attorney);
  const isActive = attorney.status === "active";

  return (
    <div className="space-y-6">
      <Breadcrumb />

      {/* Header */}
      <div className="mt-4 lg:mt-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Heading>{displayName}</Heading>
            {attorney.firmName && (
              <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                {attorney.firmName}
              </p>
            )}
            <div className="mt-2">
              <Badge color={isActive ? "green" : "zinc"}>
                {isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <EditAttorneyButton attorney={attorney} />
            {isActive && <DeactivateAttorneyButton attorney={attorney} />}
          </div>
        </div>
      </div>

      {/* Contact & Professional Info */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Contact Information */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <Subheading>Contact Information</Subheading>
          <Divider className="my-4" />
          <DescriptionList>
            <DescriptionTerm>Email</DescriptionTerm>
            <DescriptionDetails>
              {attorney.email ? (
                <a
                  href={`mailto:${attorney.email}`}
                  className="text-blue-600 hover:underline dark:text-blue-400"
                >
                  {attorney.email}
                </a>
              ) : (
                "Not provided"
              )}
            </DescriptionDetails>

            <DescriptionTerm>Office Phone</DescriptionTerm>
            <DescriptionDetails>
              {attorney.phoneNumber || "Not provided"}
            </DescriptionDetails>

            <DescriptionTerm>Mobile Phone</DescriptionTerm>
            <DescriptionDetails>
              {attorney.mobilePhone || "Not provided"}
            </DescriptionDetails>

            {attorney.website && (
              <>
                <DescriptionTerm>Website</DescriptionTerm>
                <DescriptionDetails>
                  <a
                    href={attorney.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {attorney.website}
                  </a>
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>
        </div>

        {/* Professional Information */}
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <Subheading>Professional Information</Subheading>
          <Divider className="my-4" />
          <DescriptionList>
            <DescriptionTerm>Bar Number</DescriptionTerm>
            <DescriptionDetails>
              {attorney.barNumber || "Not provided"}
            </DescriptionDetails>

            {attorney.barStates && attorney.barStates.length > 0 && (
              <>
                <DescriptionTerm>Licensed In</DescriptionTerm>
                <DescriptionDetails>
                  {attorney.barStates.join(", ")}
                </DescriptionDetails>
              </>
            )}

            {attorney.practiceAreas && attorney.practiceAreas.length > 0 && (
              <>
                <DescriptionTerm>Practice Areas</DescriptionTerm>
                <DescriptionDetails>
                  {attorney.practiceAreas
                    .map((area) => ATTORNEY_PRACTICE_AREA_LABELS[area])
                    .join(", ")}
                </DescriptionDetails>
              </>
            )}
          </DescriptionList>
        </div>
      </div>

      {/* Firm Address */}
      {attorney.firmAddress && (
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <Subheading>Firm Address</Subheading>
          <Divider className="my-4" />
          <p className="text-sm text-zinc-700 dark:text-zinc-300">
            {attorney.firmAddress.streetAddress}
            {attorney.firmAddress.streetAddress2 && `, ${attorney.firmAddress.streetAddress2}`}
            <br />
            {attorney.firmAddress.city}, {attorney.firmAddress.state}{" "}
            {attorney.firmAddress.zipCode}
          </p>
        </div>
      )}

      {/* Legal Aid / Assistant */}
      {(attorney.legalAidName || attorney.legalAidEmail || attorney.legalAidPhone) && (
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <Subheading>Legal Aid / Assistant</Subheading>
          <Divider className="my-4" />
          <DescriptionList>
            {attorney.legalAidName && (
              <>
                <DescriptionTerm>Name</DescriptionTerm>
                <DescriptionDetails>{attorney.legalAidName}</DescriptionDetails>
              </>
            )}
            {attorney.legalAidEmail && (
              <>
                <DescriptionTerm>Email</DescriptionTerm>
                <DescriptionDetails>
                  <a
                    href={`mailto:${attorney.legalAidEmail}`}
                    className="text-blue-600 hover:underline dark:text-blue-400"
                  >
                    {attorney.legalAidEmail}
                  </a>
                </DescriptionDetails>
              </>
            )}
            {attorney.legalAidPhone && (
              <>
                <DescriptionTerm>Phone</DescriptionTerm>
                <DescriptionDetails>{attorney.legalAidPhone}</DescriptionDetails>
              </>
            )}
          </DescriptionList>
        </div>
      )}

      {/* Notes */}
      {attorney.notes && (
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <Subheading>Notes</Subheading>
          <Divider className="my-4" />
          <p className="text-sm text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap">
            {attorney.notes}
          </p>
        </div>
      )}

      {/* Cases List */}
      <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
        <AttorneyCasesList attorneyId={attorney.id} />
      </div>
    </div>
  );
};

export default AttorneyDetailPage;
